import { toast } from 'sonner';
import { BuybackToastBody } from './BuybackToastBody';
import { isNotificationShown } from './visibility';

/**
 * `true` when the iframe is loaded as the Design Constructor preview
 * child (`?design_constructor=child`). Used to keep toasts pinned while
 * the operator tweaks knobs — sonner's default ~4s auto-dismiss would
 * otherwise rip the pill away mid-edit.
 */
export function isInDesignConstructor(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('design_constructor') === 'child';
}

/**
 * Buyback confirmation toast — Figma 1251:1131. Fire after an Accept
 * Buyback succeeds anywhere in the app (reveal flow, My Packs). Renders
 * the shared `Notification` pill through sonner's custom-toast slot so it
 * inherits the app's `<Toaster>` placement + auto-dismiss, and the look
 * stays identical everywhere.
 *
 * In the Design Constructor preview the pill is rendered with an
 * infinite duration and a click-to-dismiss handler — the operator
 * needs to keep it on-screen while editing knobs, and dismisses it
 * manually with a click once done. End-user runs use sonner's default
 * auto-dismiss. An operator can turn the toast off in the constructor.
 *
 * @param amount Gross buyback amount credited to the balance.
 */
export function notifyBuyback(amount: number) {
  if (!isNotificationShown('--ui-notification-buyback')) return;
  const inEditor = isInDesignConstructor();
  toast.custom(
    (t) => (
      <div
        onClick={inEditor ? () => toast.dismiss(t) : undefined}
        role={inEditor ? 'button' : undefined}
        style={inEditor ? { cursor: 'pointer' } : undefined}
        title={inEditor ? 'Click to dismiss (editor only)' : undefined}
      >
        <BuybackToastBody amount={amount} />
      </div>
    ),
    {
      // Buyback pill sits in the top-left, separate from centred error toasts.
      position: 'top-left',
      ...(inEditor ? { duration: Infinity } : {}),
    },
  );
}
